import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { VaultCardSetup } from '@/app/activate-account/components/VaultCardSetup'
import {
	useCompleteBillingSetup,
	useCreateBillingSetupSession,
	type BillingSetupSession,
} from '@/lib/api/billing'
import { currentPageReturnUrl } from '@/lib/billing-card-setup-return'

/**
 * Add or replace the billing card of a vault-billed account (Checkout.com)
 * from Billing > Payment methods. Same form and same server flow as the
 * activation page; there is no provider-hosted portal for these accounts.
 * Saving a card retries the account's open invoices right away (server side).
 */
export function VaultCardDialog({
	open,
	onOpenChange,
	forPendingProvider = false,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Collect the card on the provider an account manager is moving the account to. */
	forPendingProvider?: boolean
}) {
	const createSetupSession = useCreateBillingSetupSession()
	const completeSetup = useCompleteBillingSetup()
	const [session, setSession] = useState<BillingSetupSession | null>(null)
	const [isFinalizing, setIsFinalizing] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		setError(null)
		setIsFinalizing(false)
		createSetupSession.mutate(
			{ forPendingProvider },
			{
				onSuccess: (data) => setSession(data),
				onError: (e) => setError(e.message || 'Could not start card setup. Please try again.'),
			},
		)
		// Runs on open only: the mutation object is stable enough for that.
	}, [open, forPendingProvider])

	const handleTokenized = (token: string) => {
		if (!session) return
		setIsFinalizing(true)
		setError(null)
		completeSetup.mutate(
			{ provider: session.provider, token, returnUrl: currentPageReturnUrl() },
			{
				onSuccess: (result) => {
					if (result.status === 'requires_action') {
						window.location.assign(result.redirectUrl)
						return
					}
					toast.success('Your card is saved.')
					onOpenChange(false)
				},
				onError: (e) => {
					setIsFinalizing(false)
					setError(e.message || 'Could not save your card. Please try again.')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Billing card</DialogTitle>
					<DialogDescription>
						This card pays your weekly TagadaPay invoices. It replaces the card on file once your bank
						approves it.
					</DialogDescription>
				</DialogHeader>
				{session && session.provider !== 'stripe' ? (
					<VaultCardSetup
						publicKey={session.vault.publicKey}
								tokenPurpose={session.vault.tokenPurpose}
						onTokenized={handleTokenized}
						isFinalizing={isFinalizing}
						error={error}
						submitLabel="Save card"
					/>
				) : error ? (
					<p className="text-destructive py-6 text-center text-sm">{error}</p>
				) : null}
			</DialogContent>
		</Dialog>
	)
}
